import { scaleLinear } from 'd3-scale';
import { pointer, Selection } from 'd3-selection';
import { curveCardinal, line } from 'd3-shape';

import { AbstractComponent, Config as BaseConfig, Params, Size } from '../abstract.component';

export enum OriginsStepEnum {
  DEFAULT = 1,
  PREDICTIVE = 2,
  REFINEMENT = 3,
  DATA = 4
}

enum OriginCategoryEnum {
  DEFAULT = 'default',
  PREDICTIVE = 'predictive',
  REFINEMENT = 'refinement'
}

enum OriginTextTypeEnum {
  TITLE = 'title',
  COUNT = 'count'
}

enum OriginCircleTypeEnum {
  VALUE = 'value',
  MAX = 'max',
  SOLID = 'solid'
}

enum OriginPathTypeEnum {
  INITIAL = 'initial',
  DATA = 'data',
}

export class OriginsComponent extends AbstractComponent<Config, Data> {

  private static readonly COMPONENT_CLASS = 'epb-origins-tree';

  private static readonly V_PITCH_IN_PIXELS = 170;
  private static readonly BAR_PITCH_X_IN_PIXELS = 0;
  private static readonly DEFAULT_PITCH_X_IN_PIXELS = 0;

  private static readonly LEGEND_CIRCLE_RADIUS = 6;

  private static readonly PREDICTIVE_COLOR_IN_HSL = [193, 97, 35];
  private static readonly REFINEMENT_COLOR_IN_HSL = [41, 98, 67];
  private static readonly COLOR_LIGHTNESS_MAX_DIFF = 15;

  private static readonly TOOLTIP_WIDTH_IN_PIXELS = 200;
  private static readonly TOOLTIP_MOUSE_OFFSET = 8;
  private static readonly TOOLTIP_TRANSITIONS_DURATION_IN_MILLIS = 200;

  // --- Elements for SVG and Groups
  private svg: Selection<any, any, any, any>;
  private dataviz: Selection<any, any, any, any>;
  private tooltip: Selection<any, any, any, any>;

  private size: Size;

  private currentStep: OriginsStepEnum;

  // --- Common Style Settings for all the dataviz

  constructor(containerEl: HTMLElement, params: Params<Config, Data> = {}) {
    super(containerEl, params);
    this.containerElement.classed(OriginsComponent.COMPONENT_CLASS, true);

    this.size = this.getElementSize(containerEl);

    if (!!this.data) {
      requestAnimationFrame(() => {
        this.draw();
        this.setCurrentStep(OriginsStepEnum.DEFAULT);
      });
    }
  }

  public setCurrentStep(step: OriginsStepEnum): void {
    this.currentStep = step;
    requestAnimationFrame(() => {
      Object.values(OriginCategoryEnum).forEach((category: OriginCategoryEnum) => {
        Object.values(OriginTextTypeEnum).forEach((textType: OriginTextTypeEnum) => {
          this.dataviz.selectAll(
            `.${ OriginsComponent.COMPONENT_CLASS }__text--${ category }.${ OriginsComponent.COMPONENT_CLASS }__text--${ textType }`)
            .classed(`${ OriginsComponent.COMPONENT_CLASS }__text--visible`, this.isTextVisible(category, textType));
        });
        Object.values(OriginCircleTypeEnum).forEach((circleType: OriginCircleTypeEnum) => {
          this.dataviz.selectAll(
            `.${ OriginsComponent.COMPONENT_CLASS }__circle--${ category }.${ OriginsComponent.COMPONENT_CLASS }__circle--${ circleType }`)
            .classed(`${ OriginsComponent.COMPONENT_CLASS }__circle--visible`, this.isCircleVisible(category, circleType));
        });
        Object.values(OriginPathTypeEnum).forEach((pathType: OriginPathTypeEnum) => {
          this.dataviz.selectAll(
            `.${ OriginsComponent.COMPONENT_CLASS }__path--${ category }.${ OriginsComponent.COMPONENT_CLASS }__path--${ pathType }`)
            .classed(`${ OriginsComponent.COMPONENT_CLASS }__path--visible`, this.isPathVisible(category, pathType));
        });
      });
    });
  }

  public update(params: Params<Config, Data>): void {
    super.update(params);
    this.redraw();
  }

  protected resize(size: Size): void {
    super.resize(size);
    this.size = size;
    this.redraw();
  }

  private draw(): void {
    // ----- Read the size of the window
    const { height, width } = this.size;

    // ----- Define the svg and the groups
    this.svg = this.containerElement
      .append('svg')
      .attr('width', '100%')
      .attr('height', '100%');

    this.dataviz = this.svg.append('g')
      .style('transform', 'translate(50%,0%)');

    // ----- Line Generator & Interpolation

    const lineGenerator = line()
      .curve(curveCardinal.tension(0.5));

    // ----- Spatial span of the branches

    function thedelicate() {
      const delicate = (Math.random() * 20) - 10;
      return delicate;
    }

    // ----- Distribute input data into arrays

    const retrieveValues = [];
    const retrieveId = [];

    this.data.origins.forEach((value, index) => {
      retrieveValues.push(this.data.origins[index].queryCount);
      retrieveId.push(this.data.origins[index].id);
    });

    // ----- Sort origins by branch

    let theMobile = [0];
    const predictive = [];
    const refinement = [];
    const predictiveName = [];
    const refinementName = [];

    const listDefault = this.config.defaultOriginIds;
    const listPredictive = this.config.predictiveOriginIds;
    const listRefinement = this.config.refinementOriginIds;

    retrieveId.forEach((value, index) => {
      const check = listDefault.includes(value);
      if (check === true) {
        theMobile = [retrieveValues[index]];
      } else {
        const turnPred = listPredictive.includes(value);
        const turnRef = listRefinement.includes(value);
        if (turnPred === true) {
          predictive.push(retrieveValues[index]);
          predictiveName.push(retrieveId[index]);
        } else if (turnRef === true) {
          refinement.push(retrieveValues[index]);
          refinementName.push(retrieveId[index]);
        }
      }
    });

    const dataCatPred = predictiveName;
    const dataCatRef = refinementName;
    const maxBranchesPerCategory = Math.max(dataCatPred.length, dataCatRef.length);
    const colorScaleDomain = [0, (maxBranchesPerCategory / 2), maxBranchesPerCategory];
    const predictiveColor = scaleLinear()
      .domain(colorScaleDomain)
      .range(this.getHSLColorRange(OriginsComponent.PREDICTIVE_COLOR_IN_HSL));
    const refinementColor = scaleLinear()
      .domain(colorScaleDomain)
      .range(this.getHSLColorRange(OriginsComponent.REFINEMENT_COLOR_IN_HSL));

    // ----- Calculate the maximum metric for resizing the tree accordingly

    const calcTheMobilePred = predictive;
    const calcTheMobileRef = refinement;
    const calcTheMobile = theMobile;

    const estimate = calcTheMobilePred.concat(calcTheMobileRef, calcTheMobilePred, theMobile);
    const adjustment = Math.max.apply(Math, estimate);
    const magnify = 2.5 * (7 ** (Math.log10(adjustment) - 1));

    // ----- Calculate the metrics in percentages

    function add(accumulator, a) {
      return accumulator + a;
    }

    const estimate1 = calcTheMobilePred.concat(calcTheMobileRef, theMobile);
    const sumEstimate1 = estimate1.reduce(add, 0);

    const perTheMobilePred = calcTheMobilePred.map(function (item) {
      return item / sumEstimate1;
    });
    const perTheMobileRef = calcTheMobileRef.map(function (item) {
      return item / sumEstimate1;
    });
    const perTheMobile = calcTheMobile.map(function (item) {
      return item / sumEstimate1;
    });

    let theMobilePred;
    let theMobileRef;
    let perPred;
    let perRef;

    theMobilePred = calcTheMobilePred;
    theMobileRef = calcTheMobileRef;
    perPred = perTheMobilePred;
    perRef = perTheMobileRef;

    let mobile;
    // ----- Cumulative arrays for the position of each branch
    if (theMobile[0] === 0) {
      mobile = [0];
    } else {
      mobile = theMobile.map(function (item) {
        return item / magnify;
      });
    }
    const mobilePred = theMobilePred.map(function (item) {
      return item / magnify;
    });
    const mobileRef = theMobileRef.map(function (item) {
      return item / magnify;
    });

    const allTotalPred = [0];
    let theSumPred = 0;
    mobilePred.forEach((value, index) => {
      theSumPred = theSumPred + mobilePred[index];
      allTotalPred.push(theSumPred);
    });

    const allTotalValPred = [0];
    let theSumValPred = 0;
    theMobilePred.forEach((value, index) => {
      theSumValPred = theSumValPred + theMobilePred[index];
      allTotalValPred.push(theSumValPred);
    });

    const allTotalRef = [0];
    let theSum = 0;
    mobileRef.forEach((value, index) => {
      theSum = theSum + mobileRef[index];
      allTotalRef.push(theSum);
    });

    const allTotalValRef = [0];
    let theSumValRef = 0;
    theMobileRef.forEach((value, index) => {
      theSumValRef = theSumValRef + theMobileRef[index];
      allTotalValRef.push(theSumValRef);
    });

    // ----- Separated spatially adaptable group and subgroups for each tree

    const spot = this.dataviz.append('g');
    this.tooltip = this.svg
      .append('foreignObject')
      .attr('class', `${ OriginsComponent.COMPONENT_CLASS }__tooltip`)
      .style('opacity', 0);
    this.tooltip
      .append('xhtml:div')
      .attr('class', `${ OriginsComponent.COMPONENT_CLASS }__tooltip-content-wrapper`)
      .append('span')
      .attr('class', `${ OriginsComponent.COMPONENT_CLASS }__tooltip-content`)
      .style('width', `${ OriginsComponent.TOOLTIP_WIDTH_IN_PIXELS }px`);
    const gdefault = spot.append('g');
    const gpredictiveback = spot
      .append('g')
      .attr('class', `${ OriginsComponent.COMPONENT_CLASS }__predictive-initial`);
    const gpredictive = spot.append('g');
    const grefinementback = spot
      .append('g')
      .attr('class', `${ OriginsComponent.COMPONENT_CLASS }__refinement-initial`);
    const grefinement = spot.append('g');
    const gcircles = spot
      .append('g')
      .attr('class', `${ OriginsComponent.COMPONENT_CLASS }__circles`);
    const glegend = spot.append('g')
      .attr('class', `${ OriginsComponent.COMPONENT_CLASS }__legend`);

    // ----- Vertical flexibility

    const bottomPitch = (height <= 700) ? 370 : (height - 330);

    glegend
      .append('text')
      .attr('class', `${ OriginsComponent.COMPONENT_CLASS }__text ${ OriginsComponent.COMPONENT_CLASS }__text--legend`)
      .attr('x', OriginsComponent.DEFAULT_PITCH_X_IN_PIXELS + mobile[0] / 2 - 180)
      .attr('y', OriginsComponent.V_PITCH_IN_PIXELS + bottomPitch)
      .text(() => `≤ ${ magnify.toLocaleString(this.config.locale, { maximumFractionDigits: 0 }) } queries`);

    glegend
      .append('text')
      .attr('class', `${ OriginsComponent.COMPONENT_CLASS }__text ${ OriginsComponent.COMPONENT_CLASS }__text--legend`)
      .attr('x', OriginsComponent.DEFAULT_PITCH_X_IN_PIXELS + mobile[0] / 2 - 50)
      .attr('y', OriginsComponent.V_PITCH_IN_PIXELS + bottomPitch)
      .text('Organic (log scale)');

    glegend
      .append('text')
      .attr('class', `${ OriginsComponent.COMPONENT_CLASS }__text ${ OriginsComponent.COMPONENT_CLASS }__text--legend`)
      .attr('x', OriginsComponent.DEFAULT_PITCH_X_IN_PIXELS + mobile[0] / 2 + 90)
      .attr('y', OriginsComponent.V_PITCH_IN_PIXELS + bottomPitch)
      .text('Metric queries (log scale)');

    glegend
      .append('line')
      .attr('class', `${ OriginsComponent.COMPONENT_CLASS }__line ${ OriginsComponent.COMPONENT_CLASS }__line--legend`)
      .attr('x1', OriginsComponent.DEFAULT_PITCH_X_IN_PIXELS + mobile[0] / 2 - 190)
      .attr('y1', OriginsComponent.V_PITCH_IN_PIXELS + bottomPitch - 4)
      .attr('x2', OriginsComponent.DEFAULT_PITCH_X_IN_PIXELS + mobile[0] / 2 - 210)
      .attr('y2', OriginsComponent.V_PITCH_IN_PIXELS + bottomPitch - 4);

    glegend
      .append('line')
      .attr('class',
        `${ OriginsComponent.COMPONENT_CLASS }__line ${ OriginsComponent.COMPONENT_CLASS }__line--legend ${ OriginsComponent.COMPONENT_CLASS }__line--x-axis`)
      .attr('x1', OriginsComponent.DEFAULT_PITCH_X_IN_PIXELS - 200)
      .attr('y1', OriginsComponent.V_PITCH_IN_PIXELS + bottomPitch - 30)
      .attr('x2', OriginsComponent.DEFAULT_PITCH_X_IN_PIXELS + 240)
      .attr('y2', OriginsComponent.V_PITCH_IN_PIXELS + bottomPitch - 30)
      .attr('stroke-width', 4);

    glegend
      .append('circle')
      .attr('class',
        `${ OriginsComponent.COMPONENT_CLASS }__circle ${ OriginsComponent.COMPONENT_CLASS }__circle--legend ${ OriginsComponent.COMPONENT_CLASS }__circle--max`)
      .attr('r', OriginsComponent.LEGEND_CIRCLE_RADIUS)
      .attr('cx', OriginsComponent.DEFAULT_PITCH_X_IN_PIXELS + mobile[0] / 2 - 60)
      .attr('cy', OriginsComponent.V_PITCH_IN_PIXELS + bottomPitch - 4);

    glegend
      .append('circle')
      .attr('class',
        `${ OriginsComponent.COMPONENT_CLASS }__circle ${ OriginsComponent.COMPONENT_CLASS }__circle--legend ${ OriginsComponent.COMPONENT_CLASS }__circle--metric`)
      .attr('r', OriginsComponent.LEGEND_CIRCLE_RADIUS)
      .attr('cx', OriginsComponent.DEFAULT_PITCH_X_IN_PIXELS + mobile[0] / 2 + 80)
      .attr('cy', OriginsComponent.V_PITCH_IN_PIXELS + bottomPitch - 4);

    // ----- Predictive branches
    for (let i = 0; i < dataCatPred.length; i++) {

      const predictivePoints = [
        [
          OriginsComponent.DEFAULT_PITCH_X_IN_PIXELS - allTotalPred[i] - mobilePred[i] / 2 - mobilePred[i],
          OriginsComponent.V_PITCH_IN_PIXELS + bottomPitch - 30
        ],
        [
          OriginsComponent.DEFAULT_PITCH_X_IN_PIXELS - allTotalPred[i] - mobilePred[i] / 2 - mobilePred[i],
          OriginsComponent.V_PITCH_IN_PIXELS + bottomPitch - 170 - (dataCatPred.length - i) * 30
        ],
        [
          OriginsComponent.BAR_PITCH_X_IN_PIXELS - (i + 2) * 20 - 10 - allTotalPred[i] - mobilePred[i] / 2
          - mobilePred[i], OriginsComponent.V_PITCH_IN_PIXELS - (dataCatPred.length - i) * 30 + bottomPitch - 270
        ],
        [
          OriginsComponent.BAR_PITCH_X_IN_PIXELS - (i + 2) * 20 - 10 - allTotalPred[i] - mobilePred[i] / 2
          - mobilePred[i], this.getTipPositionInPixels(i) + (5 + 2 * Math.log(mobile[0]))
        ]
      ];

      const predictivePathScrolly = lineGenerator(predictivePoints);

      const predictivePathNZL = gpredictiveback
        .append('path')
        .attr('class', () => this.getPathClass(OriginCategoryEnum.PREDICTIVE, OriginPathTypeEnum.INITIAL))
        .attr('d', predictivePathScrolly);

      gpredictiveback
        .append('path')
        .attr('class', () => this.getPathClass(OriginCategoryEnum.PREDICTIVE, OriginPathTypeEnum.INITIAL)
          .concat(` ${ OriginsComponent.COMPONENT_CLASS }__path--dashed`))
        .attr('d', predictivePathScrolly);

      const totalLengthPredictiveNZL = predictivePathNZL.node().getTotalLength();

      predictivePathNZL
        .attr('stroke-dasharray', totalLengthPredictiveNZL + ' ' + totalLengthPredictiveNZL)
        .attr('stroke-dashoffset', totalLengthPredictiveNZL);

    }

    dataCatPred.forEach((cat, index) => {

      gcircles
        .append('circle')
        .attr('class', () => this.getCircleClass(OriginCategoryEnum.PREDICTIVE, OriginCircleTypeEnum.VALUE))
        .attr('r', () => {
          const value = 5 + 2 * Math.log(mobilePred[index]);
          return (value > 0 ? value : 0);
        })
        .attr('cx', OriginsComponent.BAR_PITCH_X_IN_PIXELS - (index + 2) * 20 - (allTotalPred[index]) - 10 - mobilePred[index] / 2)
        .attr('cy', this.getTipPositionInPixels(index))
        .attr('stroke', predictiveColor(index))
        .attr('fill', predictiveColor(index));

      gcircles
        .append('circle')
        .attr('class', () => this.getCircleClass(OriginCategoryEnum.PREDICTIVE, OriginCircleTypeEnum.MAX))
        .attr('r', 5 + 2 * Math.log(mobile[0]))
        .attr('cx', OriginsComponent.BAR_PITCH_X_IN_PIXELS - (index + 2) * 20 - (allTotalPred[index]) - 10 - mobilePred[index] / 2)
        .attr('cy', this.getTipPositionInPixels(index));

      for (let k = 0; k < mobilePred[index]; k++) {

        const rand = thedelicate();

        const points = [
          [
            OriginsComponent.DEFAULT_PITCH_X_IN_PIXELS - allTotalPred[index] - mobilePred[index] / 2 - mobilePred[index] * rand / 20,
            OriginsComponent.V_PITCH_IN_PIXELS + bottomPitch - 30
          ],
          [
            OriginsComponent.DEFAULT_PITCH_X_IN_PIXELS - allTotalPred[index] - mobilePred[index] / 2 - mobilePred[index] * rand / 15,
            OriginsComponent.V_PITCH_IN_PIXELS + bottomPitch - 170 - (dataCatPred.length - index) * 30
          ],
          [
            OriginsComponent.BAR_PITCH_X_IN_PIXELS - (index + 2) * 20 - 10 - allTotalPred[index] - mobilePred[index] / 2
            - mobilePred[index] * rand / 15, OriginsComponent.V_PITCH_IN_PIXELS - (dataCatPred.length - index) * 30 + bottomPitch - 270
          ],
          [
            OriginsComponent.BAR_PITCH_X_IN_PIXELS - (index + 2) * 20 - 10 - allTotalPred[index] - mobilePred[index] / 2
            - mobilePred[index] * rand / 40, this.getTipPositionInPixels(index)
          ]
        ];

        const pathData = lineGenerator(points);

        const pathNZL = gpredictive
          .append('path')
          .attr('class', () => this.getPathClass(OriginCategoryEnum.PREDICTIVE, OriginPathTypeEnum.DATA))
          .attr('d', pathData)
          .style('stroke', predictiveColor(index));

        const totalLengthNZL = pathNZL.node().getTotalLength();

        pathNZL
          .attr('stroke-dasharray', totalLengthNZL + ' ' + totalLengthNZL)
          .attr('stroke-dashoffset', totalLengthNZL);
      }

      const predictiveOriginInfo = gpredictive
        .append('g')
        .attr('class',
          `${ OriginsComponent.COMPONENT_CLASS }__origin-info ${ OriginsComponent.COMPONENT_CLASS }__origin-info--${ OriginCategoryEnum.PREDICTIVE }`)
        .on('mouseover', () => this.onOriginInfoMouseOver(cat))
        .on('mousemove', (event: MouseEvent) => this.onOriginInfoMouseMove(pointer(event, this.svg.node())))
        .on('mouseout', () => this.onOriginInfoMouseOut());
      predictiveOriginInfo
        .append('text')
        .attr('class', () => this.getTextClass(OriginCategoryEnum.PREDICTIVE, OriginTextTypeEnum.TITLE))
        .attr('x', -30 + OriginsComponent.BAR_PITCH_X_IN_PIXELS - (index + 2) * 20 - (allTotalPred[index]) - 10 - mobilePred[index] / 2)
        .attr('y', -5 + this.getTipPositionInPixels(index))
        .text(dataCatPred[index]);

      predictiveOriginInfo
        .append('text')
        .attr('class', () => this.getTextClass(OriginCategoryEnum.PREDICTIVE, OriginTextTypeEnum.COUNT))
        .attr('x', -30 + OriginsComponent.BAR_PITCH_X_IN_PIXELS - (index + 2) * 20 - (allTotalPred[index]) - 10 - mobilePred[index] / 2)
        .attr('y', 10 + this.getTipPositionInPixels(index))
        .text(() => `${ theMobilePred[index].toLocaleString(this.config.locale) } queries`);

    });

    // ------ Refinement branches
    for (let i = 0; i < dataCatRef.length; i++) {

      const refinementPoints = [
        [
          OriginsComponent.DEFAULT_PITCH_X_IN_PIXELS + mobile[0] + allTotalRef[i] + mobileRef[i] / 2
          + mobileRef[i],
          OriginsComponent.V_PITCH_IN_PIXELS + bottomPitch - 30
        ],
        [
          OriginsComponent.DEFAULT_PITCH_X_IN_PIXELS + mobile[0] + allTotalRef[i] + mobileRef[i] / 2
          + mobileRef[i],
          OriginsComponent.V_PITCH_IN_PIXELS + bottomPitch - 150 - (dataCatRef.length - i) * 20
        ],
        [
          OriginsComponent.BAR_PITCH_X_IN_PIXELS + mobile[0] + (i + 2) * 20 + 10 + allTotalRef[i] + mobileRef[i] / 2
          + mobileRef[i], OriginsComponent.V_PITCH_IN_PIXELS - (dataCatRef.length - i) * 20 + bottomPitch - 220
        ],
        [
          OriginsComponent.BAR_PITCH_X_IN_PIXELS + mobile[0] + (i + 2) * 20 + 10 + allTotalRef[i] + mobileRef[i] / 2
          + mobileRef[i], this.getTipPositionInPixels(i) + (5 + 2 * Math.log(mobile[0]))
        ]
      ];

      const refinementPathScrolly = lineGenerator(refinementPoints);

      const refinementPathNZL = grefinementback
        .append('path')
        .attr('class', () => this.getPathClass(OriginCategoryEnum.REFINEMENT, OriginPathTypeEnum.INITIAL))
        .attr('d', refinementPathScrolly);

      grefinementback
        .append('path')
        .attr('class', () => this.getPathClass(OriginCategoryEnum.REFINEMENT, OriginPathTypeEnum.INITIAL)
          .concat(` ${ OriginsComponent.COMPONENT_CLASS }__path--dashed`))
        .attr('d', refinementPathScrolly);

      const totalLengthRefinementNZL = refinementPathNZL.node().getTotalLength();

      refinementPathNZL
        .attr('stroke-dasharray', totalLengthRefinementNZL + ' ' + totalLengthRefinementNZL)
        .attr('stroke-dashoffset', totalLengthRefinementNZL);

    }

    dataCatRef.forEach((cat, index) => {
      gcircles
        .append('circle')
        .attr('class', () => this.getCircleClass(OriginCategoryEnum.REFINEMENT, OriginCircleTypeEnum.VALUE))
        .attr('r', () => {
          const value = 5 + 2 * Math.log(mobilePred[index]);
          return (value > 0 ? value : 0);
        })
        .attr('cx', OriginsComponent.BAR_PITCH_X_IN_PIXELS + mobile[0] + (index + 2) * 20 + 10 + allTotalRef[index] + mobileRef[index] / 2)
        .attr('cy', this.getTipPositionInPixels(index))
        .attr('stroke', refinementColor(index))
        .attr('fill', refinementColor(index));

      gcircles
        .append('circle')
        .attr('class', () => this.getCircleClass(OriginCategoryEnum.REFINEMENT, OriginCircleTypeEnum.MAX))
        .attr('r', 5 + 2 * Math.log(mobile[0]))
        .attr('cx', OriginsComponent.BAR_PITCH_X_IN_PIXELS + mobile[0] + (index + 2) * 20 + 10 + allTotalRef[index] + mobileRef[index] / 2)
        .attr('cy', this.getTipPositionInPixels(index));

      for (let k = 0; k < mobileRef[index]; k++) {

        const rand = thedelicate();
        const rand2 = thedelicate();
        const rand3 = thedelicate();

        const points = [
          [
            OriginsComponent.DEFAULT_PITCH_X_IN_PIXELS + mobile[0] + allTotalRef[index] + mobileRef[index] / 2
            + mobileRef[index] * rand / 20,
            OriginsComponent.V_PITCH_IN_PIXELS + bottomPitch - 30
          ],
          [
            OriginsComponent.DEFAULT_PITCH_X_IN_PIXELS + mobile[0] + allTotalRef[index] + mobileRef[index] / 2
            + mobileRef[index] * rand3 / 15,
            OriginsComponent.V_PITCH_IN_PIXELS + bottomPitch - 150 - (dataCatRef.length - index) * 20
          ],
          [
            OriginsComponent.BAR_PITCH_X_IN_PIXELS + mobile[0] + (index + 2) * 20 + 10 + allTotalRef[index] + mobileRef[index] / 2
            + mobileRef[index] * rand3 / 15, OriginsComponent.V_PITCH_IN_PIXELS - (dataCatRef.length - index) * 20 + bottomPitch - 220
          ],
          [
            OriginsComponent.BAR_PITCH_X_IN_PIXELS + mobile[0] + (index + 2) * 20 + 10 + allTotalRef[index] + mobileRef[index] / 2
            + mobileRef[index] * rand2 / 80, this.getTipPositionInPixels(index)
          ]
        ];

        const pathData = lineGenerator(points);

        const pathNZL = grefinement
          .append('path')
          .attr('class', () => this.getPathClass(OriginCategoryEnum.REFINEMENT, OriginPathTypeEnum.DATA))
          .attr('d', pathData)
          .style('stroke', refinementColor(index));

        const totalLengthNZL = pathNZL.node().getTotalLength();

        pathNZL
          .attr('stroke-dasharray', totalLengthNZL + ' ' + totalLengthNZL)
          .attr('stroke-dashoffset', totalLengthNZL);
      }

      const refinementOriginInfo = grefinement
        .append('g')
        .attr('class',
          `${ OriginsComponent.COMPONENT_CLASS }__origin-info ${ OriginsComponent.COMPONENT_CLASS }__origin-info--${ OriginCategoryEnum.REFINEMENT }`)
        .on('mouseover', () => this.onOriginInfoMouseOver(cat))
        .on('mousemove', (event: MouseEvent) => this.onOriginInfoMouseMove(pointer(event, this.svg.node())))
        .on('mouseout', () => this.onOriginInfoMouseOut());

      refinementOriginInfo
        .append('text')
        .attr('class', () => this.getTextClass(OriginCategoryEnum.REFINEMENT, OriginTextTypeEnum.TITLE))
        .attr('x',
          20 + OriginsComponent.BAR_PITCH_X_IN_PIXELS + mobile[0] + (index + 2) * 20 + 20 + allTotalRef[index] + mobileRef[index] / 2)
        .attr('y', -5 + this.getTipPositionInPixels(index))
        .text(dataCatRef[index]);

      refinementOriginInfo
        .append('text')
        .attr('class', () => this.getTextClass(OriginCategoryEnum.REFINEMENT, OriginTextTypeEnum.COUNT))
        .attr('x',
          20 + OriginsComponent.BAR_PITCH_X_IN_PIXELS + mobile[0] + (index + 2) * 20 + 20 + allTotalRef[index] + mobileRef[index] / 2)
        .attr('y', 10 + this.getTipPositionInPixels(index))
        .text(() => `${ theMobileRef[index].toLocaleString(this.config.locale) } queries`);

    });

    // ----- Organic Branch

    gcircles
      .append('circle')
      .attr('class', () => this.getCircleClass(OriginCategoryEnum.DEFAULT, OriginCircleTypeEnum.SOLID))
      .attr('r', 5 + 2 * Math.log(mobile[0]))
      .attr('cx', OriginsComponent.DEFAULT_PITCH_X_IN_PIXELS + mobile[0] / 2)
      .attr('cy', OriginsComponent.V_PITCH_IN_PIXELS + 70);

    const defaultPoints = [
      [
        OriginsComponent.DEFAULT_PITCH_X_IN_PIXELS + mobile[0] / 2,
        OriginsComponent.V_PITCH_IN_PIXELS + bottomPitch - 30
      ],
      [
        OriginsComponent.DEFAULT_PITCH_X_IN_PIXELS + mobile[0] / 2,
        OriginsComponent.V_PITCH_IN_PIXELS + 300
      ],
      [
        OriginsComponent.DEFAULT_PITCH_X_IN_PIXELS + mobile[0] / 2,
        OriginsComponent.V_PITCH_IN_PIXELS + 70 + (5 + 2 * Math.log(mobile[0]))
      ]
    ];

    const defaultPathScrolly = lineGenerator(defaultPoints);

    const defaultPathNZL = gdefault
      .append('path')
      .attr('class', () => this.getPathClass(OriginCategoryEnum.DEFAULT, OriginPathTypeEnum.INITIAL))
      .attr('d', defaultPathScrolly);
    gdefault
      .append('path')
      .attr('class', () => this.getPathClass(OriginCategoryEnum.DEFAULT, OriginPathTypeEnum.INITIAL)
        .concat(` ${ OriginsComponent.COMPONENT_CLASS }__path--dashed`))
      .attr('d', defaultPathScrolly);

    const totalLengthDefaultNZL = defaultPathNZL.node().getTotalLength();

    defaultPathNZL
      .attr('stroke-dasharray', totalLengthDefaultNZL + ' ' + totalLengthDefaultNZL)
      .attr('stroke-dashoffset', totalLengthDefaultNZL);

    for (let k = 0; k < mobile[0]; k++) {

      const rand = thedelicate();
      const rand2 = thedelicate();

      const points = [
        [
          OriginsComponent.DEFAULT_PITCH_X_IN_PIXELS + mobile[0] / 2 + (mobile[0] / 2) * rand / 10,
          OriginsComponent.V_PITCH_IN_PIXELS + bottomPitch - 30
        ],
        [
          OriginsComponent.DEFAULT_PITCH_X_IN_PIXELS + mobile[0] / 2 + (mobile[0] / 2) * rand / 12,
          OriginsComponent.V_PITCH_IN_PIXELS + 300
        ],
        [
          OriginsComponent.DEFAULT_PITCH_X_IN_PIXELS + mobile[0] / 2 + (mobile[0] / 2) * rand / 20,
          OriginsComponent.V_PITCH_IN_PIXELS + 72
        ]
      ];

      const pathData = lineGenerator(points);

      const pathNZL = gdefault
        .append('path')
        .attr('class', () => this.getPathClass(OriginCategoryEnum.DEFAULT, OriginPathTypeEnum.DATA))
        .attr('d', pathData);

      const totalLengthNZL = pathNZL.node().getTotalLength();

      pathNZL
        .attr('stroke-dasharray', totalLengthNZL + ' ' + totalLengthNZL)
        .attr('stroke-dashoffset', totalLengthNZL);
    }

    const defaultOriginInfo = gdefault
      .append('g')
      .attr('class',
        `${ OriginsComponent.COMPONENT_CLASS }__origin-info ${ OriginsComponent.COMPONENT_CLASS }__origin-info--${ OriginCategoryEnum.DEFAULT }`)
      .on('mouseover', () => this.onOriginInfoMouseOver(listDefault[0]))
      .on('mousemove', (event: MouseEvent) => this.onOriginInfoMouseMove(pointer(event, this.svg.node())))
      .on('mouseout', () => this.onOriginInfoMouseOut());

    defaultOriginInfo
      .append('text')
      .attr('class', () => this.getTextClass(OriginCategoryEnum.DEFAULT, OriginTextTypeEnum.TITLE))
      .attr('x', OriginsComponent.DEFAULT_PITCH_X_IN_PIXELS + mobile[0] / 2)
      .attr('y', OriginsComponent.V_PITCH_IN_PIXELS + 20)
      .text(function () {
        if (theMobile[0] === 0) {
          return ' ';
        } else {
          return 'ORGANIC';
        }
      });

    defaultOriginInfo
      .append('text')
      .attr('class', () => this.getTextClass(OriginCategoryEnum.DEFAULT, OriginTextTypeEnum.COUNT))
      .attr('x', OriginsComponent.DEFAULT_PITCH_X_IN_PIXELS + mobile[0] / 2)
      .attr('y', OriginsComponent.V_PITCH_IN_PIXELS + 35)
      .text(() => `${ theMobile[0].toLocaleString(this.config.locale) } queries`);

  }

  private redraw(): void {
    this.containerElement.selectAll('*').remove();
    if (!!this.data) {
      if (this.currentStep === OriginsStepEnum.DATA) {
        this.currentStep--;
        this.draw();
        this.setCurrentStep(OriginsStepEnum.DATA);
      } else {
        this.draw();
      }
    }
  }

  private getHSLColorRange(hslColor: number[]): string[] {
    const hue = hslColor[0];
    const saturation = hslColor[1];
    const lightness = hslColor[2];
    return [
      `hsl(${ hue }, ${ saturation }%, ${ lightness + OriginsComponent.COLOR_LIGHTNESS_MAX_DIFF }%)`,
      `hsl(${ hue }, ${ saturation }%, ${ lightness }%)`,
      `hsl(${ hue }, ${ saturation }%, ${ lightness - OriginsComponent.COLOR_LIGHTNESS_MAX_DIFF }%)`
    ];
  }

  private getTextClass(category: OriginCategoryEnum, textType: OriginTextTypeEnum): string {
    const baseClass = `${ OriginsComponent.COMPONENT_CLASS }__text`;
    const classList = [baseClass, `${ baseClass }--${ category }`, `${ baseClass }--${ textType }`];
    if (this.isTextVisible(category, textType)) {
      classList.push(`${ baseClass }--visible`);
    }
    return classList.join(' ');
  }

  private isTextVisible(category: OriginCategoryEnum, textType: OriginTextTypeEnum): boolean {
    return (textType === OriginTextTypeEnum.TITLE && (this.getOriginCategoryStep(category) <= this.currentStep)) ||
      (this.currentStep === OriginsStepEnum.DATA);
  }

  private getCircleClass(category: OriginCategoryEnum, circleType: OriginCircleTypeEnum): string {
    const baseClass = `${ OriginsComponent.COMPONENT_CLASS }__circle`;
    const classList = [baseClass, `${ baseClass }--${ category }`, `${ baseClass }--${ circleType }`];
    if (this.isCircleVisible(category, circleType)) {
      classList.push(`${ baseClass }--visible`);
    }
    return classList.join(' ');
  }

  private isCircleVisible(category: OriginCategoryEnum, circleType: OriginCircleTypeEnum): boolean {
    return (circleType === OriginCircleTypeEnum.MAX && (this.getOriginCategoryStep(category) <= this.currentStep)) ||
      (this.currentStep === OriginsStepEnum.DATA);
  }

  private getPathClass(category: OriginCategoryEnum, pathType: OriginPathTypeEnum): string {
    const baseClass = `${ OriginsComponent.COMPONENT_CLASS }__path`;
    const classList = [baseClass, `${ baseClass }--${ category }`, `${ baseClass }--${ pathType }`];
    if (this.isPathVisible(category, pathType)) {
      classList.push(`${ baseClass }--visible`);
    }
    return classList.join(' ');
  }

  private isPathVisible(category: OriginCategoryEnum, pathType: OriginPathTypeEnum): boolean {
    return (pathType === OriginPathTypeEnum.INITIAL && (this.getOriginCategoryStep(category) <= this.currentStep)) ||
      (this.currentStep === OriginsStepEnum.DATA);
  }

  private getOriginCategoryStep(category: OriginCategoryEnum): OriginsStepEnum {
    return {
      [OriginCategoryEnum.DEFAULT]: OriginsStepEnum.DEFAULT,
      [OriginCategoryEnum.PREDICTIVE]: OriginsStepEnum.PREDICTIVE,
      [OriginCategoryEnum.REFINEMENT]: OriginsStepEnum.REFINEMENT
    }[category];
  }

  private getTipPositionInPixels(index: number): number {
    const sepp = 42;
    const PitchV = 170;
    return PitchV - 120 + index * sepp;
  }

  private getOriginMessages(originId: string) {
    return this.config.messages.origins[originId];
  }

  private onOriginInfoMouseOver(originId: string): void {
    const originMessages = this.getOriginMessages(originId);
    if (!!originMessages && !!originMessages.description) {
      this.tooltip
        .transition().duration(OriginsComponent.TOOLTIP_TRANSITIONS_DURATION_IN_MILLIS)
        .style('opacity', 1);
      this.tooltip
        .select(`.${ OriginsComponent.COMPONENT_CLASS }__tooltip-content`)
        .text(originMessages.description);
    }
  }

  private onOriginInfoMouseMove(mousePosition: number[]): void {
    this.tooltip
      .style('transform', `translate3d(${ mousePosition[0] }px, ${ mousePosition[1] }px, 0)`);
    this.tooltip
      .select(`.${ OriginsComponent.COMPONENT_CLASS }__tooltip-content`)
      .style('transform', `translate3d( ${ this.getTooltipContentTranslateX(mousePosition[0]) }, 0, 0)`);
  }

  private getTooltipContentTranslateX(mousePositionX: number): string {
    const translateX = `${ OriginsComponent.TOOLTIP_MOUSE_OFFSET }px`;
    if ((mousePositionX + OriginsComponent.TOOLTIP_WIDTH_IN_PIXELS) > this.size.width) {
      return `calc(-100% - ${ translateX })`;
    }
    return translateX;
  }

  private onOriginInfoMouseOut(): void {
    this.tooltip
      .transition().duration(OriginsComponent.TOOLTIP_TRANSITIONS_DURATION_IN_MILLIS)
      .style('opacity', 0);
  }

  protected getElementSize(element: HTMLElement): Size {
    return {
      height: element.getBoundingClientRect().height,
      width: element.getBoundingClientRect().width
    };
  }
}

export interface Config extends BaseConfig {
  defaultOriginIds: string[];
  predictiveOriginIds: string[];
  refinementOriginIds: string[];
  currentStep: OriginsStepEnum;
  messages: OriginsComponentMessages;
}

export interface OriginsComponentMessages {
  origins: {
    [origin: string]: {
      description?: string
    };
  };
}

export type Data = OriginsData;

export interface OriginsData {
  queryCount: number;
  origins: Origin[];
}

export interface Origin {
  id: string;
  queryCount: number;
  queryCountPct: number;
}
